import React from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  Collapse,
  Link,
} from "@mui/material";
import {
  PersonOutline,
  PermIdentity,
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
  MarkEmailReadOutlined,
  Agriculture,
} from "@mui/icons-material";
import * as Yup from "yup";
import useAuth from "../hooks/useAuth";
import CalmAuthShell, {
  calmPanelSx,
  calmInputSx,
  calmPrimaryButtonSx,
} from "./CalmAuthShell";

const ink = "#2c4054";
const inkMuted = "#5a6e82";
const iconMuted = "rgba(44, 64, 84, 0.42)";

const SignupSchema = Yup.object().shape({
  firstname: Yup.string().trim().required("First name is required"),
  lastname: Yup.string().trim().required("Last name is required"),
  username: Yup.string()
    .trim()
    .min(2, "At least 2 characters")
    .max(50, "At most 50 characters")
    .required("Username is required"),
  email: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Email is required"),
  password: Yup.string()
    .trim()
    .min(6, "At least 6 characters")
    .required("Password is required"),
});

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);

  return (
    <CalmAuthShell maxWidth={480}>
      <Box component="section" sx={{ ...calmPanelSx, p: { xs: 3, sm: 4 } }}>
        <Box sx={{ textAlign: "center", mb: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.25,
            }}
          >
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL || ""}/black_on_white.png`}
              alt="OnTheFarm Today"
              loading="lazy"
              sx={{
                display: "block",
                width: { xs: 130, sm: 164 },
                height: "auto",
                objectFit: "contain",
                filter:
                  "drop-shadow(0 1px 0 rgba(255,255,255,0.3)) drop-shadow(0 3px 10px rgba(26,42,58,0.28))",
              }}
            />
          </Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 600,
              letterSpacing: "-0.02em",
              color: ink,
              fontSize: "1.25rem",
            }}
          >
            Create account
          </Typography>
          <Typography variant="body2" sx={{ color: inkMuted, mt: 0.75 }}>
            Farm owner and organization administrator
          </Typography>

          <Box
            sx={{
              mt: 2,
              mx: "auto",
              textAlign: "left",
              py: 1.5,
              px: 1.75,
              borderRadius: 1.5,
              border: "1px solid rgba(255, 255, 255, 0.38)",
              bgcolor: "rgba(255, 255, 255, 0.34)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
            }}
          >
            <Agriculture
              sx={{
                fontSize: 22,
                color: iconMuted,
                flexShrink: 0,
                mt: 0.2,
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  color: ink,
                  fontSize: "0.8125rem",
                }}
              >
                Who this form is for
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.55, color: inkMuted, fontSize: "0.8125rem" }}>
                You are registering as the person who sets up the farm and acts as its principal
                administrator (team, settings, and data for your organization).
              </Typography>
            </Box>
          </Box>

          <Alert
            severity="info"
            icon={<MarkEmailReadOutlined sx={{ fontSize: 20 }} />}
            variant="outlined"
            sx={{
              mt: 2,
              mx: "auto",
              textAlign: "left",
              alignItems: "flex-start",
              py: 1.25,
              px: 1.5,
              borderRadius: 1.5,
              borderColor: "rgba(74, 126, 176, 0.38)",
              bgcolor: "rgba(255, 255, 255, 0.36)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              color: inkMuted,
              "& .MuiAlert-icon": {
                color: "#4a7eb0",
                mr: 1,
                alignSelf: "flex-start",
                mt: 0.15,
                opacity: 0.9,
              },
              "& .MuiAlert-message": { width: "100%", padding: 0 },
            }}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{
                display: "block",
                fontWeight: 600,
                mb: 0.35,
                color: ink,
                fontSize: "0.8125rem",
              }}
            >
              Invited to an existing farm?
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: "0.8125rem" }}>
              Do not use this form. Open the link in your invitation email to join the right
              organization.
            </Typography>
          </Alert>
        </Box>

        <Formik
          initialValues={{
            firstname: "",
            lastname: "",
            username: "",
            email: "",
            password: "",
          }}
          validationSchema={SignupSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            setStatus(undefined);
            const payload = {
              firstname: values.firstname.trim(),
              lastname: values.lastname.trim(),
              username: values.username.trim(),
              email: values.email.trim(),
              password: values.password.trim(),
            };
            const result = await signup(payload);
            setSubmitting(false);
            if (result.success) {
              navigate("/check-email");
              return;
            }
            const err = result.error;
            const detail = err?.detail ?? err?.message;
            setStatus({
              serverError:
                (Array.isArray(detail)
                  ? detail.map((x) => x.msg || x).join(" ")
                  : detail) ||
                "Unable to create account. Please try again.",
            });
          }}
        >
          {({
            isSubmitting,
            errors,
            touched,
            isValid,
            dirty,
            setFieldValue,
            status,
          }) => (
            <Form noValidate>
              <Collapse in={Boolean(status?.serverError)} unmountOnExit>
                <Alert
                  severity="error"
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderRadius: 1.5,
                    bgcolor: "rgba(255, 255, 255, 0.36)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                >
                  {status?.serverError}
                </Alert>
              </Collapse>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  columnGap: 1.5,
                  rowGap: 0,
                }}
              >
                <Field
                  as={TextField}
                  name="firstname"
                  label="First name"
                  fullWidth
                  margin="dense"
                  size="small"
                  autoComplete="given-name"
                  onBlur={(e) =>
                    setFieldValue("firstname", e.target.value.trim())
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: iconMuted, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    gridColumn: { xs: "1", sm: "1" },
                    mt: 0.5,
                    ...calmInputSx,
                  }}
                  error={touched.firstname && Boolean(errors.firstname)}
                  helperText={touched.firstname && errors.firstname}
                />
                <Field
                  as={TextField}
                  name="lastname"
                  label="Last name"
                  fullWidth
                  margin="dense"
                  size="small"
                  autoComplete="family-name"
                  onBlur={(e) =>
                    setFieldValue("lastname", e.target.value.trim())
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: iconMuted, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    gridColumn: { xs: "1", sm: "2" },
                    mt: 0.5,
                    ...calmInputSx,
                  }}
                  error={touched.lastname && Boolean(errors.lastname)}
                  helperText={touched.lastname && errors.lastname}
                />
              </Box>

              <Field
                as={TextField}
                name="username"
                label="Username"
                fullWidth
                margin="dense"
                size="small"
                autoComplete="username"
                onBlur={(e) =>
                  setFieldValue("username", e.target.value.trim())
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PermIdentity sx={{ color: iconMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1.5, ...calmInputSx }}
                error={touched.username && Boolean(errors.username)}
                helperText={touched.username && errors.username}
              />

              <Field
                as={TextField}
                name="email"
                label="Email"
                fullWidth
                margin="dense"
                size="small"
                autoComplete="email"
                onBlur={(e) => setFieldValue("email", e.target.value.trim())}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: iconMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1.5, ...calmInputSx }}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />

              <Field
                as={TextField}
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="dense"
                size="small"
                autoComplete="new-password"
                onBlur={(e) =>
                  setFieldValue("password", e.target.value.trim())
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: iconMuted, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((s) => !s)}
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ color: iconMuted, fontSize: 20 }} />
                        ) : (
                          <Visibility sx={{ color: iconMuted, fontSize: 20 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mt: 1.5, ...calmInputSx }}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disableElevation
                disabled={isSubmitting || !isValid || !dirty}
                sx={{ ...calmPrimaryButtonSx, mt: 2 }}
              >
                {isSubmitting ? "Creating account…" : "Create account"}
              </Button>

              <Box sx={{ mt: 2.5, textAlign: "center" }}>
                <Typography variant="body2" sx={{ color: inkMuted, fontSize: "0.875rem" }}>
                  Already have an account?{" "}
                  <Link component={RouterLink} to="/login" underline="hover" sx={{ color: ink, fontWeight: 600 }}>
                    Sign in
                  </Link>
                </Typography>
              </Box>
              <Box sx={{ mt: 1.5, textAlign: "center" }}>
                <Link
                  component={RouterLink}
                  to="/"
                  underline="hover"
                  sx={{ fontSize: "0.8125rem", color: inkMuted }}
                >
                  About OnTheFarm Today
                </Link>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </CalmAuthShell>
  );
}
